import { supabase, withRetry } from './supabase';
import { taskLogger } from './taskLogger';

// Helper function to get safe origin
export function getSafeOrigin(): string {
  try {
    if (typeof window !== 'undefined' && window.location && window.location.hostname.includes('webcontainer')) {
      return 'https://sms.aiagenthub.pl';
    }
    
    if (typeof window !== 'undefined' && window.location) {
      try {
        return window.location.origin;
      } catch (e) {
        return 'https://sms.aiagenthub.pl';
      }
    }

    return 'https://sms.aiagenthub.pl';
  } catch (error) {
    return 'https://sms.aiagenthub.pl';
  }
}

// Helper function to get public URL for images
export async function getPublicImageUrl(imageUrl: string | null): Promise<string | null> {
  if (!imageUrl || imageUrl === 'Brak' || imageUrl.trim() === '') {
    return null;
  }

  try {
    if (imageUrl.startsWith('data:')) {
      return null;
    }

    if (imageUrl.includes('sklep.aderlo.pl')) {
      return imageUrl;
    }

    if (imageUrl.startsWith('http') && !imageUrl.includes('supabase.co')) {
      return imageUrl;
    }

    if (imageUrl.includes('supabase.co')) {
      try {
        const url = new URL(imageUrl);
        const pathParts = url.pathname.split('/');
        const uploadsIndex = pathParts.findIndex(part => part === 'uploads');
        if (uploadsIndex === -1) {
          return null;
        }
        const filePath = pathParts.slice(uploadsIndex).join('/');
        
        const { data } = supabase.storage
          .from('post-images')
          .getPublicUrl(filePath);

        return data?.publicUrl || null;
      } catch (error) {
        console.error('Error parsing Supabase URL:', error);
        return null;
      }
    }

    if (!imageUrl.startsWith('http')) {
      const filePath = imageUrl.startsWith('uploads/') ? imageUrl : `uploads/${imageUrl}`;
      const { data } = supabase.storage
        .from('post-images')
        .getPublicUrl(filePath);

      return data?.publicUrl || null;
    }

    return null;
  } catch (error) {
    console.error('Error getting public URL:', error);
    return null;
  }
}

// Helper function to log webhook operations
export async function logWebhookOperation(
  type: string,
  details: Record<string, any>
) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    await supabase
      .from('scheduled_tasks_log')
      .insert([{
        event_type: type,
        details: {
          ...details,
          user_id: user?.id,
          timestamp: new Date().toISOString()
        }
      }]);
  } catch (error) {
    taskLogger.error('Error logging webhook operation:', {
      error: error instanceof Error ? error.message : 'Unknown error',
      type,
      details
    });
  }
}

// Helper function to make webhook requests with retries
export async function makeWebhookRequest(url: string, data: any, timeout: number = 300000): Promise<any> {
  const maxRetries = 3;
  const baseDelay = 2000; // 2 seconds
  let attempt = 0;
  let lastError: Error | null = null;

  while (attempt < maxRetries) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        throw new Error('Request timeout exceeded');
      }, timeout);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': getSafeOrigin()
        },
        body: JSON.stringify(data),
        signal: controller.signal,
        credentials: 'omit'
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Webhook failed with status ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result || !result.data) {
        throw new Error('Invalid webhook response format');
      }

      return result;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      if (error.name === 'AbortError' || error.message === 'Request timeout exceeded') {
        lastError = new Error('Request timeout exceeded');
        break;
      }
      
      attempt++;
      if (attempt < maxRetries) {
        const delay = Math.min(baseDelay * Math.pow(2, attempt) + Math.random() * 1000, 10000);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      break;
    }
  }

  throw lastError || new Error('Webhook request failed after retries');
}

interface CreatePostWebhookPayload {
  taskId: string;
  content: string;
  platform: string;
  accountId: string;
  imageUrl?: string;
  generateImage: boolean;
  modifyImage: boolean;
  isTest?: boolean;
  publishDate?: string;
}

export async function sendCreatePostWebhook(webhookUrl: string, payload: CreatePostWebhookPayload) {
  try {
    if (!webhookUrl) {
      throw new Error('No webhook URL provided');
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('Not authenticated');
    }

    taskLogger.info('Starting create post webhook', {
      taskId: payload.taskId,
      platform: payload.platform,
      isTest: payload.isTest
    });

    // For test posts, call the webhook directly instead of using the database
    if (payload.isTest) {
      try {
        // Get public URL for image if needed
        let publicImageUrl = null;
        if (payload.imageUrl) {
          publicImageUrl = await getPublicImageUrl(payload.imageUrl);
        }

        // Prepare webhook data
        const webhookData = {
          text: payload.content,
          account_id: payload.accountId,
          platform: payload.platform,
          generate_image: payload.generateImage,
          use_image_as_reference: payload.modifyImage,
          tool_id: payload.taskId,
          usage_id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          is_test: true,
          image_url: publicImageUrl,
          // Add the requested information
          generate: payload.generateImage,
          modify: payload.modifyImage,
          photo: !!publicImageUrl,
          test: true,
          webhookUrl: webhookUrl,
          executionMode: 'test'
        };

        // Make direct webhook request
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Origin': window.location.origin
          },
          body: JSON.stringify(webhookData)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Webhook failed with status ${response.status}: ${errorText}`);
        }

        // Parse the response - handle both array and object formats
        const rawResult = await response.json();
        
        // Check if response is an array and extract the first item
        const webhookResponse = Array.isArray(rawResult) ? rawResult[0] : rawResult;
        
        if (!webhookResponse || !webhookResponse.data) {
          throw new Error('Invalid webhook response format');
        }

        // Create a post record
        const { data: testPost, error: insertError } = await supabase
          .from('posts')
          .insert({
            content: webhookResponse.data?.content || payload.content,
            platform: payload.platform,
            published_at: new Date().toISOString(),
            image_url: webhookResponse.data?.image_url || publicImageUrl,
            task_id: payload.taskId,
            user_id: user.id,
            status: 'ready',
            metadata: {
              webhook_response: webhookResponse,
              is_test: true,
              stage: 'ready',
              is_in_progress: false,
              creation_timestamp: new Date().toISOString(),
              webhook_complete_timestamp: new Date().toISOString()
            }
          })
          .select()
          .single();

        if (insertError) {
          throw new Error(`Failed to create test post: ${insertError.message}`);
        }

        // Now that we have a valid post record, we can safely create the webhook log
        try {
          await supabase
            .from('webhook_logs')
            .insert({
              user_id: user.id,
              task_id: payload.taskId,
              post_id: testPost.id,
              webhook_url: webhookUrl,
              status: 'success',
              event_type: 'test_post',
              request_payload: webhookData
            });
        } catch (logError) {
          console.error('Failed to create webhook log for test post:', logError);
          // Don't throw here as the post was created successfully
        }

        return testPost;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown webhook error';
        throw new Error(`Test webhook request failed: ${errorMessage}`);
      }
    }

    // For regular posts, use the dedicated function
    try {
      const { data: post, error: createError } = await supabase.rpc('create_direct_post', {
        p_task_id: payload.taskId,
        p_user_id: user.id,
        p_content: payload.content,
        p_image_url: payload.imageUrl,
        p_is_test: false
      });

      if (createError) {
        throw createError;
      }

      return post;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      taskLogger.error('Error creating post', {
        taskId: payload.taskId,
        error: errorMessage,
        timestamp: new Date().toISOString(),
        details: error
      });
      throw error;
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    taskLogger.error('Error in create post webhook', {
      taskId: payload.taskId,
      error: errorMessage,
      timestamp: new Date().toISOString(),
      details: error
    });
    throw error;
  }
}

interface PublishPostWebhookPayload {
  taskId: string;
  content: string;
  platform: string;
  accountId: string;
  imageUrl?: string;
  generateImage: boolean;
  modifyImage: boolean;
  fullModifyImage?: boolean;
}

export async function sendPublishPostWebhook(webhookUrl: string, payload: PublishPostWebhookPayload) {
  try {
    if (!webhookUrl) {
      throw new Error('No webhook URL configured');
    }

    if (!payload.taskId || !payload.platform || !payload.accountId) {
      throw new Error('Missing required parameters');
    }

    console.log('Starting publish request for task:', payload.taskId);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('Not authenticated');
    }

    // Call the manual_publish_post function
    const { data, error } = await supabase.rpc('manual_publish_post', {
      p_task_id: payload.taskId,
      p_user_id: user.id
    });

    if (error) {
      console.error('Error calling manual_publish_post:', error);
      throw new Error(`Failed to start publishing: ${error.message}`);
    }

    if (!data || !data.success) {
      throw new Error(data?.error || 'Unknown error occurred during publishing');
    }

    return data;
  } catch (error) {
    console.error('Error in publish webhook:', error);
    throw error;
  }
}